/**
 * Bank Transactions Documents Panel — per-document view with extraction metadata
 * and transactions table showing both original and agent-classified types.
 *
 * Each document is a collapsible section. Transactions support search, date
 * filtering, and group-by with collapsible group headers.
 */

import React, { Fragment, useState, useMemo, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useAction } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Check,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  Landmark,
  Search,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Pencil,
  X,
  Trash2,
  GitMerge,
  Undo2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/ui/primitives/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/ui/composed/overlays/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import { TransactionDetailSheet } from "./TransactionDetailSheet";
import { ColumnsMenu } from "./ColumnsMenu";
import type { ColumnOption } from "./ColumnsMenu";
import { useHiddenColumns } from "./ColumnsMenu.helpers";
import { Input } from "@/ui/primitives/input";
import { SlideOverPanel } from "../shared-panels/SlideOverPanel";
import { ChangeHistory } from "../shared-panels/ChangeHistory";
import type {
  ChangeHistoryEntry,
  ResolvedHistoryUser,
} from "../shared-panels/ChangeHistory";
import {
  BANK_TRANSACTION_FIELD_FORMATTERS,
  BANK_TRANSACTION_FIELD_LABELS,
} from "./historyFormatters";
import { DocumentDetail } from "../../documents/DocumentDetail";
import { TypeBadge, DirectionBadge, CounterpartyBadge } from "./badges";
import { TYPE_LABELS } from "./badges.helpers";

/**
 * Categories the practitioner can pick from in the bulk re-categorise toolbar.
 * Mirrors the per-row dropdown in `TransactionDetailSheet` — `related_party`
 * is intentionally omitted because that classification is owned by a separate
 * downstream agent.
 */
const BULK_CATEGORY_OPTIONS = [
  { value: "employee", label: "Possible Employee" },
  { value: "trade_third_parties", label: "Trade & Third Parties" },
  { value: "government_statutory", label: "Government & Statutory" },
  { value: "financial_institutions", label: "Financial Institutions" },
  { value: "utilities_rent", label: "Utilities & Rent" },
  { value: "internal_intercompany", label: "Internal/Inter-company" },
  { value: "professional_advisors", label: "Professional Advisors" },
  { value: "software_subscriptions", label: "Software & Subscriptions" },
  { value: "insurance", label: "Insurance" },
  { value: "fuel", label: "Fuel" },
  { value: "retail", label: "Retail" },
  { value: "travel", label: "Travel" },
  { value: "unknown", label: "Unknown" },
] as const;

type BulkCategory = (typeof BULK_CATEGORY_OPTIONS)[number]["value"];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DocAccountPeriod {
  periodFrom?: string;
  periodTo?: string;
  openingBalance?: number;
  closingBalance?: number;
}

/**
 * Per-document account row, deduped server-side. Each row represents one
 * `(institution, accountNumber, accountType)` combination. If the extractor
 * produced multiple statement periods for the same account in this document
 * they're collapsed here — `periodFrom`/`periodTo` span all periods,
 * `openingBalance` is from the earliest, `closingBalance` from the latest, and
 * the original spans live on `periods` for an expand-to-detail view.
 */
interface DocAccount {
  institution?: string;
  accountNumber?: string;
  accountName?: string;
  cardholderName?: string;
  bsb?: string;
  accountType?: string;
  periodFrom?: string;
  periodTo?: string;
  openingBalance?: number;
  closingBalance?: number;
  statementPeriods?: number;
  periods?: DocAccountPeriod[];
}

interface DocumentMeta {
  documentId: string;
  fileName: string;
  entity?: {
    name?: string;
    identifiers?: Array<{ type: string; value: string }>;
  };
  accounts?: DocAccount[];
  statedSummary?: {
    totalDebits?: number;
    totalCredits?: number;
    transactionCount?: number;
  };
  transactionCount: number;
}

/**
 * Canonical account row produced by `getCanonicalBankAccounts` — one row per
 * real `(institution, accountNumber)` regardless of how many documents or
 * statement periods contributed. Built deterministically from
 * `bankTransactionEntries` and enriched with name / type / BSB candidates
 * from extraction metadata.
 */
interface CanonicalAccount {
  institution?: string;
  accountNumber?: string;
  accountName?: string;
  cardholderName?: string;
  accountType?: string;
  bsb?: string;
  transactionCount: number;
  totalDebits: number;
  totalCredits: number;
  totalAmount: number;
  /** Closing balance from the most recent statement period for this account. */
  closingBalance?: number;
  firstTxnDate?: string;
  lastTxnDate?: string;
  sourceDocumentIds: string[];
  /** Distinct row-own keys folded into this account — used by merge/delete. */
  memberKeys?: string[];
  changeHistory?: ChangeHistoryEntry[];
}

interface BankTxnEntry {
  _id: string;
  date: string;
  amount: number;
  direction: "debit" | "credit";
  description: string;
  counterpartyName?: string;
  extractionCounterpartyName?: string;
  reference?: string;
  balanceAfter?: number;
  accountInstitution?: string;
  accountNumber?: string;
  transactionType: string;
  extractionTransactionType?: string;
  typeSource: string;
  sourceDocumentId?: string;
  sourceFileName?: string;
  notes?: string;
  isManualOverride?: boolean;
  changeHistory: Array<{
    timestamp: number;
    actor: string;
    action: string;
    reason: string;
    previousValues?: Record<string, unknown>;
  }>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type GroupByKey = "none" | "direction" | "counterparty" | "category";

const GROUP_BY_OPTIONS: Array<{ value: GroupByKey; label: string }> = [
  { value: "none", label: "No grouping" },
  { value: "direction", label: "Direction" },
  { value: "counterparty", label: "Counterparty" },
  { value: "category", label: "Category" },
];

type SortField = "date" | "amount" | null;
type SortDir = "asc" | "desc";

const PAGE_SIZE = 100;

const fmt = (n: number) =>
  "$" +
  n.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Toggleable columns for the canonical accounts summary table (Documents tab). */
const ACCOUNT_COLUMNS: ColumnOption[] = [
  { key: "accountName", label: "Account Name" },
  { key: "accountNumber", label: "Account No." },
  { key: "holder", label: "Holder" },
  { key: "type", label: "Type" },
  { key: "institution", label: "Institution" },
  { key: "txns", label: "Txns" },
  { key: "totalDebits", label: "Total Debits" },
  { key: "totalCredits", label: "Total Credits" },
  { key: "closingBalance", label: "Closing Balance" },
  { key: "dates", label: "Dates" },
];

const formatAccountType = (type: string | undefined): string => {
  if (!type || type === "-") return "-";
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const fmtShort = (n: number) =>
  "$" + n.toLocaleString("en-AU", { minimumFractionDigits: 0 });

function formatDate(date: string): string {
  if (!date) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [y, m, d] = date.split("-");
    return `${d}/${m}/${y}`;
  }
  return date;
}

const MONTH_MAP: Record<string, string> = {
  jan: "01",
  feb: "02",
  mar: "03",
  apr: "04",
  may: "05",
  jun: "06",
  jul: "07",
  aug: "08",
  sep: "09",
  oct: "10",
  nov: "11",
  dec: "12",
};

function toSortableDate(date: string): string {
  if (!date) return "";
  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  // DD/MM/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
    const [d, m, y] = date.split("/");
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  // D Mon YYYY or DD Mon YYYY (e.g. "31 Aug 2020", "1 Jan 2021")
  const match = date.match(/^(\d{1,2})\s+(\w{3,})\s+(\d{4})$/);
  if (match) {
    const [, d, mon, y] = match;
    const m = MONTH_MAP[mon.slice(0, 3).toLowerCase()];
    if (m) return `${y}-${m}-${d.padStart(2, "0")}`;
  }
  return date;
}

function earlierDate(a?: string, b?: string): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return toSortableDate(a) <= toSortableDate(b) ? a : b;
}

function laterDate(a?: string, b?: string): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return toSortableDate(a) >= toSortableDate(b) ? a : b;
}

/**
 * Normalises an account number for client-side dedup — strips non-alphanumeric
 * chars and strips a 6-digit BSB prefix from numbers >= 12 digits.
 */
function normalizeAccountNumber(value: string | undefined): string {
  if (!value) return "";
  const stripped = value.replace(/[^A-Za-z0-9]/g, "").toLowerCase();
  if (!stripped) return "";
  if (/x/i.test(stripped)) return stripped.replace(/^.*x/i, "") || stripped;
  if (/^\d+$/.test(stripped) && stripped.length >= 12) return stripped.slice(6);
  return stripped;
}

/**
 * Merge accounts that share the same normalised account number into a single
 * row. Handles BSB-prefixed variants and partial numbers by using suffix
 * matching. Period From = earliest, Period To = latest, all original periods
 * are collected for the expandable detail view.
 */
function dedupeAccountsByNumber(accounts: DocAccount[]): DocAccount[] {
  const grouped = new Map<string, DocAccount[]>();
  for (const acct of accounts) {
    const key = normalizeAccountNumber(acct.accountNumber);
    const existing = grouped.get(key);
    if (existing) existing.push(acct);
    else grouped.set(key, [acct]);
  }

  // Suffix-merge: collapse keys where one is a suffix of the other.
  const keys = Array.from(grouped.keys()).sort((a, b) => b.length - a.length);
  const removed = new Set<string>();
  for (let i = 0; i < keys.length; i++) {
    if (removed.has(keys[i])) continue;
    for (let j = i + 1; j < keys.length; j++) {
      if (removed.has(keys[j])) continue;
      if (keys[j].length < 4) continue;
      const isSuffix = keys[i].endsWith(keys[j]);
      const sharedSuffix =
        !isSuffix &&
        keys[j].length >= 6 &&
        keys[i].length >= 6 &&
        keys[i].slice(-6) === keys[j].slice(-6);
      if (isSuffix || sharedSuffix) {
        // Both keys came from `grouped.keys()` above and neither has been
        // removed yet, so both entries are guaranteed to be present.
        const groupI = grouped.get(keys[i]);
        const groupJ = grouped.get(keys[j]);
        if (groupI && groupJ) {
          groupI.push(...groupJ);
          grouped.delete(keys[j]);
          removed.add(keys[j]);
        }
      }
    }
  }

  const result: DocAccount[] = [];
  for (const group of grouped.values()) {
    if (group.length === 1) {
      result.push(group[0]);
      continue;
    }

    const allPeriods: DocAccountPeriod[] = [];
    for (const acct of group) {
      if (acct.periods && acct.periods.length > 0) {
        allPeriods.push(...acct.periods);
      } else {
        allPeriods.push({
          periodFrom: acct.periodFrom,
          periodTo: acct.periodTo,
          openingBalance: acct.openingBalance,
          closingBalance: acct.closingBalance,
        });
      }
    }
    const mergedFrom = group.reduce<string | undefined>(
      (acc, acct) => earlierDate(acc, acct.periodFrom),
      undefined,
    );
    const mergedTo = group.reduce<string | undefined>(
      (acc, acct) => laterDate(acc, acct.periodTo),
      undefined,
    );

    const first = group[0];
    const longestName = group.reduce(
      (best, a) =>
        (a.accountName?.length ?? 0) > (best.accountName?.length ?? 0)
          ? a
          : best,
      first,
    );

    result.push({
      ...first,
      accountName: longestName.accountName,
      periodFrom: mergedFrom,
      periodTo: mergedTo,
      statementPeriods: allPeriods.length,
      periods: allPeriods,
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Group-by helpers
// ---------------------------------------------------------------------------

function getGroupKey(txn: BankTxnEntry, groupBy: GroupByKey): string {
  const isAgentOrUser = txn.typeSource === "agent" || txn.typeSource === "user";
  switch (groupBy) {
    case "direction":
      return txn.direction;
    case "counterparty": {
      const best = isAgentOrUser
        ? (txn.counterpartyName ?? txn.extractionCounterpartyName)
        : (txn.extractionCounterpartyName ?? txn.counterpartyName);
      return best ?? "Uncategorised";
    }
    case "category":
      return txn.transactionType;
    default:
      return "";
  }
}

function getGroupLabel(key: string, groupBy: GroupByKey): string {
  if (groupBy === "category") {
    return TYPE_LABELS[key] ?? key;
  }
  return key;
}

// ---------------------------------------------------------------------------
// Collapsible Section
// ---------------------------------------------------------------------------

function Section({
  icon,
  title,
  count,
  defaultOpen = true,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="-mx-1 flex w-full items-center gap-1.5 rounded px-1 py-0.5 transition-colors hover:bg-muted/30"
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        <span className="flex items-center gap-1.5">
          {icon}
          <span className="text-sm font-medium">{title}</span>
        </span>
        {count !== undefined && count > 0 && (
          <Badge variant="secondary" size="sm" className="ml-auto">
            {count}
          </Badge>
        )}
      </button>
      {open && <div className="mt-2 space-y-2">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transaction Table Headers (sortable Date & Amount)
// ---------------------------------------------------------------------------

function SortIcon({
  field,
  sortField,
  sortDir,
}: {
  field: SortField;
  sortField: SortField;
  sortDir: SortDir;
}) {
  if (sortField !== field)
    return <ArrowUpDown className="ml-0.5 h-3 w-3 opacity-40" />;
  return sortDir === "asc" ? (
    <ArrowUp className="ml-0.5 h-3 w-3" />
  ) : (
    <ArrowDown className="ml-0.5 h-3 w-3" />
  );
}

function TableHeaders({
  sortField,
  sortDir,
  onSort,
}: {
  sortField: SortField;
  sortDir: SortDir;
  onSort: (field: SortField) => void;
}) {
  return (
    <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
      <th
        className="cursor-pointer select-none whitespace-nowrap px-3 py-1.5 text-left font-medium transition-colors hover:text-foreground"
        onClick={() => onSort("date")}
      >
        <span className="inline-flex items-center">
          Date
          <SortIcon field="date" sortField={sortField} sortDir={sortDir} />
        </span>
      </th>
      <th className="whitespace-nowrap px-3 py-1.5 text-left font-medium">
        Description
      </th>
      <th
        className="cursor-pointer select-none whitespace-nowrap px-3 py-1.5 text-right font-medium transition-colors hover:text-foreground"
        onClick={() => onSort("amount")}
      >
        <span className="inline-flex items-center justify-end">
          Amount
          <SortIcon field="amount" sortField={sortField} sortDir={sortDir} />
        </span>
      </th>
      <th className="whitespace-nowrap px-3 py-1.5 text-left font-medium">
        Direction
      </th>
      <th className="whitespace-nowrap px-3 py-1.5 text-right font-medium">
        Balance After
      </th>
      <th className="whitespace-nowrap px-3 py-1.5 text-left font-medium">
        Counterparty
      </th>
      <th className="whitespace-nowrap px-3 py-1.5 text-left font-medium">
        Category
      </th>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Transaction Row (click → detail sheet, no inline expand)
// ---------------------------------------------------------------------------

function TxnRow({ txn, onClick }: { txn: BankTxnEntry; onClick: () => void }) {
  const isAgentOrUser = txn.typeSource === "agent" || txn.typeSource === "user";
  const bestCounterparty = isAgentOrUser
    ? (txn.counterpartyName ?? txn.extractionCounterpartyName)
    : (txn.extractionCounterpartyName ?? txn.counterpartyName);
  const bestCategory = txn.transactionType;

  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-t border-border/50 transition-colors hover:bg-blue-50/50 dark:hover:bg-blue-950/20"
    >
      <td className="whitespace-nowrap px-3 py-1.5 text-xs tabular-nums text-muted-foreground">
        {formatDate(txn.date)}
      </td>
      <td className="max-w-[180px] truncate px-3 py-1.5 font-mono text-xs text-muted-foreground">
        {txn.description}
      </td>
      <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums">
        {txn.amount.toLocaleString("en-AU")}
      </td>
      <td className="px-3 py-1.5">
        <DirectionBadge direction={txn.direction} />
      </td>
      <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-muted-foreground">
        {txn.balanceAfter != null
          ? txn.balanceAfter.toLocaleString("en-AU")
          : "-"}
      </td>
      <td className="px-3 py-1.5">
        <CounterpartyBadge name={bestCounterparty} />
      </td>
      <td className="px-3 py-1.5">
        <TypeBadge type={bestCategory} />
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Collapsible Group (like the old PartyRow pattern)
// ---------------------------------------------------------------------------

function sortTransactions(
  txns: BankTxnEntry[],
  field: SortField,
  dir: SortDir,
): BankTxnEntry[] {
  if (!field) return txns;
  const sorted = [...txns];
  sorted.sort((a, b) => {
    if (field === "date") {
      const da = toSortableDate(a.date);
      const db = toSortableDate(b.date);
      return dir === "asc" ? da.localeCompare(db) : db.localeCompare(da);
    }
    // amount
    return dir === "asc" ? a.amount - b.amount : b.amount - a.amount;
  });
  return sorted;
}

/**
 * Modal launched from the pencil icon on a counterparty group. Lets the
 * practitioner change the category and/or rename the counterparty for every
 * transaction in the visible group in one action. Stays out of the way until
 * deliberately opened so the per-row selection in the table isn't cluttered.
 */
function EditCounterpartyGroupDialog({
  open,
  onOpenChange,
  groupKey,
  count,
  initialCategory,
  txns,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupKey: string;
  count: number;
  initialCategory: BulkCategory;
  txns: BankTxnEntry[];
}) {
  const [category, setCategory] = useState<BulkCategory>(initialCategory);
  const [counterpartyName, setCounterpartyName] = useState(groupKey);

  // Reset the form whenever the dialog is reopened so it always starts from
  // the current group state — `useState` initial values only fire on mount,
  // which would otherwise leave stale values from a previous edit.
  React.useEffect(() => {
    if (open) {
      setCategory(initialCategory);
      setCounterpartyName(groupKey);
    }
  }, [open, initialCategory, groupKey]);

  const { mutateAsync: bulkUpdate, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactions.publicMutations
        .bulkUpdateEntries,
    ),
  });

  const trimmedName = counterpartyName.trim();
  const categoryChanged = category !== initialCategory;
  const nameChanged = trimmedName !== groupKey;
  const canApply =
    (categoryChanged || nameChanged) && trimmedName.length > 0 && !isPending;

  const handleApply = async () => {
    if (!canApply) return;
    try {
      const result = await bulkUpdate({
        entryIds: txns.map((t) => t._id as Id<"bankTransactionEntries">),
        transactionType: categoryChanged ? category : undefined,
        counterpartyName: nameChanged ? trimmedName : undefined,
        groupLabel: groupKey,
      });
      if (result.updatedCount === 0) {
        toast.info(
          "No transactions changed — they already match those values.",
        );
      } else {
        toast.success(
          `Updated ${result.updatedCount} transaction${result.updatedCount === 1 ? "" : "s"} for ${groupKey}.`,
        );
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update transactions.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit counterparty group</DialogTitle>
          <DialogDescription>
            Apply changes to all {count} transaction{count === 1 ? "" : "s"} in
            this group. Manual edits to individual transactions will be
            overwritten.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Counterparty name
            </label>
            <input
              type="text"
              value={counterpartyName}
              onChange={(e) => setCounterpartyName(e.target.value)}
              placeholder="Counterparty name"
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
            {nameChanged && trimmedName.length === 0 && (
              <p className="text-xs text-destructive">
                Counterparty name cannot be empty.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Category
            </label>
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as BulkCategory)}
              disabled={isPending}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BULK_CATEGORY_OPTIONS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleApply} disabled={!canApply}>
            {isPending ? (
              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
            ) : null}
            Apply to all {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CollapsibleGroup({
  jobId,
  groupKey,
  label,
  count,
  totalCredits,
  totalDebits,
  txns,
  groupBy,
  isViewer,
  onSelectTxn,
}: {
  jobId: Id<"jobs">;
  /**
   * The raw group key (e.g. the literal counterparty value from the entries).
   * Differs from `label` only when group-by is "category", where label is the
   * pretty display name and key is the enum value. Used as the canonical
   * counterparty string for the bulk-edit action.
   */
  groupKey: string;
  label: string;
  count: number;
  totalCredits: number;
  totalDebits: number;
  txns: BankTxnEntry[];
  groupBy: GroupByKey;
  isViewer?: boolean;
  onSelectTxn: (txn: BankTxnEntry) => void;
}) {
  const net = totalCredits - totalDebits;
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [editOpen, setEditOpen] = useState(false);

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDir("desc");
      }
    },
    [sortField],
  );

  const sorted = useMemo(
    () => sortTransactions(txns, sortField, sortDir),
    [txns, sortField, sortDir],
  );
  const visible = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < txns.length;

  const { dominantCategory, dominantCounterparty } = useMemo(() => {
    const catCounts = new Map<string, number>();
    const cpCounts = new Map<string, number>();
    for (const txn of txns) {
      catCounts.set(
        txn.transactionType,
        (catCounts.get(txn.transactionType) ?? 0) + 1,
      );
      const isAgentOrUser =
        txn.typeSource === "agent" || txn.typeSource === "user";
      const cp = isAgentOrUser
        ? (txn.counterpartyName ?? txn.extractionCounterpartyName)
        : (txn.extractionCounterpartyName ?? txn.counterpartyName);
      if (cp) cpCounts.set(cp, (cpCounts.get(cp) ?? 0) + 1);
    }
    const topCat = [...catCounts.entries()].sort((a, b) => b[1] - a[1])[0];
    const topCp = [...cpCounts.entries()].sort((a, b) => b[1] - a[1])[0];
    return {
      dominantCategory: topCat?.[0],
      dominantCounterparty: topCp?.[0],
    };
  }, [txns]);

  // Pencil edit is only meaningful when grouping by counterparty and the
  // group has a real (non-uncategorised) name. The dialog applies changes to
  // exactly the rows visible in this expanded group, so the action stays
  // predictable.
  const isCounterpartyGroup =
    groupBy === "counterparty" &&
    groupKey.trim().length > 0 &&
    groupKey !== "Uncategorised";
  const initialEditCategory: BulkCategory = BULK_CATEGORY_OPTIONS.some(
    (o) => o.value === dominantCategory,
  )
    ? (dominantCategory as BulkCategory)
    : "unknown";
  const canEdit = isCounterpartyGroup && !isViewer;
  // jobId is unused locally now (dialog operates on entryIds) but threaded in
  // so future per-job actions on the group don't need a prop refactor.
  void jobId;

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="flex w-full items-center justify-between px-4 py-2.5 transition-colors hover:bg-muted/50">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <svg
            className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" />
          </svg>
          <span className="truncate text-sm font-medium">{label}</span>
          {groupBy !== "category" && dominantCategory && (
            <TypeBadge type={dominantCategory} />
          )}
          {groupBy !== "counterparty" && dominantCounterparty && (
            <CounterpartyBadge name={dominantCounterparty} />
          )}
        </button>
        <div className="ml-4 flex shrink-0 items-center gap-2">
          <Badge variant="soft" size="sm" color="gray" className="tabular-nums">
            {count} transaction{count !== 1 ? "s" : ""}
          </Badge>
          <Badge
            variant="soft"
            size="sm"
            color="green"
            className="gap-1 tabular-nums"
          >
            Credits{" "}
            <span className="font-semibold">+{fmtShort(totalCredits)}</span>
          </Badge>
          <Badge
            variant="soft"
            size="sm"
            color="red"
            className="gap-1 tabular-nums"
          >
            Debits{" "}
            <span className="font-semibold">-{fmtShort(totalDebits)}</span>
          </Badge>
          <Badge
            variant="soft"
            size="sm"
            color={net >= 0 ? "green" : "red"}
            className="gap-1 tabular-nums"
          >
            Net{" "}
            <span className="font-semibold">
              {net >= 0 ? "+" : "-"}
              {fmtShort(Math.abs(net))}
            </span>
          </Badge>
          {canEdit && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                setEditOpen(true);
              }}
              title="Edit category & counterparty for all transactions in this group"
            >
              <Pencil className="h-3.5 w-3.5" />
              <span className="sr-only">Edit group</span>
            </Button>
          )}
        </div>
      </div>

      {canEdit && (
        <EditCounterpartyGroupDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          groupKey={groupKey}
          count={count}
          initialCategory={initialEditCategory}
          txns={txns}
        />
      )}

      {isOpen && (
        <div className="border-t">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-muted/40">
                <TableHeaders
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              </thead>
              <tbody>
                {visible.map((txn) => (
                  <TxnRow
                    key={txn._id}
                    txn={txn}
                    onClick={() => onSelectTxn(txn)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <button
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              className="w-full border-t py-2 text-xs text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
            >
              Show more ({txns.length - visibleCount} remaining)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-Document Transactions (lazy loaded)
// ---------------------------------------------------------------------------

function DocumentTransactions({
  jobId,
  documentId,
  isViewer,
}: {
  jobId: Id<"jobs">;
  documentId: string;
  isViewer?: boolean;
}) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedTxn, setSelectedTxn] = useState<BankTxnEntry | null>(null);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [directionFilter, setDirectionFilter] = useState<
    "all" | "credit" | "debit"
  >("all");
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [counterpartyFilter, setCounterpartyFilter] = useState<string[]>([]);
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [groupBy, setGroupBy] = useState<GroupByKey>("counterparty");
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDir("desc");
      }
    },
    [sortField],
  );

  const { data: rawTransactions, isLoading } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listBankTransactionsForDocument,
      { jobId, sourceDocumentId: documentId },
    ),
  );

  const transactions = useMemo(
    () => (rawTransactions ?? []) as BankTxnEntry[],
    [rawTransactions],
  );

  // Filter dropdown option lists — derived from the current document's
  // transactions so the dropdowns only show categories/counterparties that
  // actually appear in this document.
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of transactions)
      if (t.transactionType) set.add(t.transactionType);
    return Array.from(set)
      .sort()
      .map((cat) => ({ value: cat, label: TYPE_LABELS[cat] ?? cat }));
  }, [transactions]);

  const counterpartyOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of transactions) {
      const name = (
        t.counterpartyName ||
        t.extractionCounterpartyName ||
        ""
      ).trim();
      if (name) set.add(name);
    }
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name, title: name }));
  }, [transactions]);

  const filtered = useMemo(() => {
    const predicates: Array<(txn: BankTxnEntry) => boolean> = [];

    if (search) {
      const q = search.toLowerCase();
      predicates.push((txn) => {
        return (
          txn.date.toLowerCase().includes(q) ||
          txn.description.toLowerCase().includes(q) ||
          String(txn.amount).includes(q) ||
          txn.direction.includes(q) ||
          (txn.counterpartyName ?? "").toLowerCase().includes(q) ||
          (txn.extractionCounterpartyName ?? "").toLowerCase().includes(q) ||
          (TYPE_LABELS[txn.transactionType] ?? "").toLowerCase().includes(q) ||
          (txn.reference ?? "").toLowerCase().includes(q)
        );
      });
    }

    if (dateFrom) {
      predicates.push((txn) => toSortableDate(txn.date) >= dateFrom);
    }
    if (dateTo) {
      predicates.push((txn) => toSortableDate(txn.date) <= dateTo);
    }

    if (directionFilter !== "all") {
      predicates.push((txn) => txn.direction === directionFilter);
    }

    if (categoryFilter.length > 0) {
      const set = new Set(categoryFilter);
      predicates.push((txn) => set.has(txn.transactionType));
    }

    if (counterpartyFilter.length > 0) {
      const set = new Set(counterpartyFilter);
      predicates.push((txn) =>
        set.has(
          (txn.counterpartyName || txn.extractionCounterpartyName || "").trim(),
        ),
      );
    }

    const minVal = amountMin ? parseFloat(amountMin) : null;
    const maxVal = amountMax ? parseFloat(amountMax) : null;
    if (minVal != null && !isNaN(minVal)) {
      predicates.push((txn) => txn.amount >= minVal);
    }
    if (maxVal != null && !isNaN(maxVal)) {
      predicates.push((txn) => txn.amount <= maxVal);
    }

    return predicates.reduce(
      (result, predicate) => result.filter(predicate),
      transactions,
    );
  }, [
    transactions,
    search,
    dateFrom,
    dateTo,
    directionFilter,
    categoryFilter,
    counterpartyFilter,
    amountMin,
    amountMax,
  ]);

  const groups = useMemo(() => {
    if (groupBy === "none") return null;

    const map = new Map<string, BankTxnEntry[]>();
    for (const txn of filtered) {
      const key = getGroupKey(txn, groupBy);
      const arr = map.get(key) ?? [];
      arr.push(txn);
      map.set(key, arr);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([key, txns]) => {
        const { credits, debits } = txns.reduce(
          (totals, t) => {
            if (t.direction === "credit") totals.credits += t.amount;
            else totals.debits += t.amount;
            return totals;
          },
          { credits: 0, debits: 0 },
        );
        return {
          key,
          label: getGroupLabel(key, groupBy),
          txns,
          totalCredits: credits,
          totalDebits: debits,
        };
      });
  }, [filtered, groupBy]);

  const sorted = useMemo(
    () => sortTransactions(filtered, sortField, sortDir),
    [filtered, sortField, sortDir],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="text-xs">Loading transactions...</span>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <p className="py-4 text-center text-xs text-muted-foreground">
        No transactions found.
      </p>
    );
  }

  const hasFilters =
    search ||
    dateFrom ||
    dateTo ||
    directionFilter !== "all" ||
    categoryFilter.length > 0 ||
    counterpartyFilter.length > 0 ||
    amountMin ||
    amountMax;
  const flatVisible = sorted.slice(0, visibleCount);
  const flatHasMore = visibleCount < sorted.length;

  return (
    <>
      {/* Toolbar — two rows so nothing is cramped */}
      <div className="mb-3 flex flex-col gap-2">
        {/* Row 1: search, dates, amounts, group by */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] max-w-[280px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search transactions..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setVisibleCount(PAGE_SIZE);
              }}
              className="h-8 w-full rounded-lg border bg-background pl-8 pr-3 text-xs placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <DatePicker
            value={dateFrom || undefined}
            onChange={(v) => {
              setDateFrom(v);
              setVisibleCount(PAGE_SIZE);
            }}
            placeholder="From"
            className="w-[150px]"
            inputClassName="h-8 text-xs rounded-lg py-0"
          />
          <DatePicker
            value={dateTo || undefined}
            onChange={(v) => {
              setDateTo(v);
              setVisibleCount(PAGE_SIZE);
            }}
            placeholder="To"
            className="w-[150px]"
            inputClassName="h-8 text-xs rounded-lg py-0"
          />
          <input
            type="number"
            placeholder="Min $"
            value={amountMin}
            onChange={(e) => {
              setAmountMin(e.target.value);
              setVisibleCount(PAGE_SIZE);
            }}
            className="h-8 w-[90px] rounded-lg border bg-background px-2 text-xs tabular-nums placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <input
            type="number"
            placeholder="Max $"
            value={amountMax}
            onChange={(e) => {
              setAmountMax(e.target.value);
              setVisibleCount(PAGE_SIZE);
            }}
            className="h-8 w-[90px] rounded-lg border bg-background px-2 text-xs tabular-nums placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Select
            value={groupBy}
            onValueChange={(v) => {
              setGroupBy(v as GroupByKey);
              setVisibleCount(PAGE_SIZE);
            }}
          >
            <SelectTrigger className="h-8 w-[150px] rounded-lg text-xs">
              <SelectValue placeholder="Group by..." />
            </SelectTrigger>
            <SelectContent>
              {GROUP_BY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasFilters && (
            <button
              onClick={() => {
                setSearch("");
                setDateFrom("");
                setDateTo("");
                setDirectionFilter("all");
                setCategoryFilter([]);
                setCounterpartyFilter([]);
                setAmountMin("");
                setAmountMax("");
              }}
              className="ml-auto text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              Clear
            </button>
          )}
        </div>

        {/* Row 2: dimension filters */}
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={directionFilter}
            onValueChange={(v) => {
              setDirectionFilter(v as "all" | "credit" | "debit");
              setVisibleCount(PAGE_SIZE);
            }}
          >
            <SelectTrigger className="h-8 w-[120px] rounded-lg text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="credit">Credits</SelectItem>
              <SelectItem value="debit">Debits</SelectItem>
            </SelectContent>
          </Select>
          <MultiSearchableSelect
            className="w-[180px]"
            value={categoryFilter}
            onValueChange={(v) => {
              setCategoryFilter(v);
              setVisibleCount(PAGE_SIZE);
            }}
            options={categoryOptions}
            placeholder="Categories"
            searchPlaceholder="Search categories..."
          />
          <MultiSearchableSelect
            className="w-[200px]"
            value={counterpartyFilter}
            onValueChange={(v) => {
              setCounterpartyFilter(v);
              setVisibleCount(PAGE_SIZE);
            }}
            options={counterpartyOptions}
            placeholder="Counterparties"
            searchPlaceholder="Search counterparties..."
          />
        </div>
      </div>

      {/* Summary bar — shows totals for filtered transactions */}
      {(() => {
        const source = hasFilters ? filtered : transactions;
        const { credits, debits } = source.reduce(
          (totals, t) => {
            if (t.direction === "credit") totals.credits += t.amount;
            else totals.debits += t.amount;
            return totals;
          },
          { credits: 0, debits: 0 },
        );
        const net = credits - debits;
        return (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {hasFilters && (
              <span className="mr-1 text-xs text-muted-foreground">
                {filtered.length} of {transactions.length} transactions
              </span>
            )}
            <Badge
              variant="soft"
              size="sm"
              color="green"
              className="gap-1 tabular-nums"
            >
              Credits{" "}
              <span className="font-semibold">+{fmtShort(credits)}</span>
            </Badge>
            <Badge
              variant="soft"
              size="sm"
              color="red"
              className="gap-1 tabular-nums"
            >
              Debits <span className="font-semibold">-{fmtShort(debits)}</span>
            </Badge>
            <Badge
              variant="soft"
              size="sm"
              color={net >= 0 ? "green" : "red"}
              className="gap-1 tabular-nums"
            >
              Net{" "}
              <span className="font-semibold">
                {net >= 0 ? "+" : "-"}
                {fmtShort(Math.abs(net))}
              </span>
            </Badge>
          </div>
        );
      })()}

      {/* Grouped view — collapsible group rows */}
      {groups ? (
        <div className="flex flex-col gap-2">
          {groups.map((g) => (
            <CollapsibleGroup
              key={g.key}
              jobId={jobId}
              groupKey={g.key}
              label={g.label}
              count={g.txns.length}
              totalCredits={g.totalCredits}
              totalDebits={g.totalDebits}
              txns={g.txns}
              groupBy={groupBy}
              isViewer={isViewer}
              onSelectTxn={setSelectedTxn}
            />
          ))}
        </div>
      ) : (
        /* Flat view — single table */
        <>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 bg-muted/40">
                <TableHeaders
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              </thead>
              <tbody>
                {flatVisible.map((txn) => (
                  <TxnRow
                    key={txn._id}
                    txn={txn}
                    onClick={() => setSelectedTxn(txn)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {flatHasMore && (
            <button
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              className="w-full rounded py-2 text-xs text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
            >
              Show more ({filtered.length - visibleCount} remaining)
            </button>
          )}
        </>
      )}

      {selectedTxn && (
        <TransactionDetailSheet
          txn={selectedTxn}
          onClose={() => setSelectedTxn(null)}
          isViewer={isViewer}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Per-Document Accounts Table (deduped + expandable periods)
// ---------------------------------------------------------------------------

/**
 * Renders the deduped accounts for a single document. Each row is one
 * `(institution, accountNumber, accountType)` combination. When the extractor
 * originally produced multiple statement periods (`statementPeriods > 1`) the
 * row carries a small badge showing the count and an expand affordance that
 * reveals the underlying spans.
 */
function DocAccountsTable({ accounts }: { accounts: DocAccount[] }) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const toggle = useCallback((i: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }, []);

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-muted/40">
          <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
            <th className="w-6 px-3 py-1.5 text-left font-medium"></th>
            <th className="px-3 py-1.5 text-left font-medium">Account Name</th>
            <th className="px-3 py-1.5 text-left font-medium">Account No.</th>
            <th className="px-3 py-1.5 text-left font-medium">Holder</th>
            <th className="px-3 py-1.5 text-left font-medium">Type</th>
            <th className="px-3 py-1.5 text-left font-medium">Institution</th>
            <th className="px-3 py-1.5 text-left font-medium" colSpan={2}>
              Dates
            </th>
            <th className="px-3 py-1.5 text-left font-medium">Statements</th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((acct, i) => {
            const periodCount = acct.statementPeriods ?? 1;
            const hasMultiple = periodCount > 1;
            const isOpen = expanded.has(i);
            return (
              <Fragment
                key={`${acct.institution ?? "?"}:${acct.accountNumber ?? "?"}:${acct.accountType ?? "?"}:${i}`}
              >
                <tr className="border-t border-border/50">
                  <td className="px-3 py-1.5">
                    {hasMultiple && (
                      <button
                        onClick={() => toggle(i)}
                        className="flex items-center text-muted-foreground transition-colors hover:text-foreground"
                        aria-label={isOpen ? "Hide periods" : "Show periods"}
                      >
                        {isOpen ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {acct.accountName ?? "-"}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">
                    {acct.accountNumber ?? "-"}
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {acct.cardholderName ?? "-"}
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {formatAccountType(acct.accountType)}
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {acct.institution ?? "-"}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs tabular-nums">
                    {formatDate(acct.periodFrom ?? "")}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs tabular-nums">
                    {formatDate(acct.periodTo ?? "")}
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    {hasMultiple ? (
                      <Badge
                        variant="secondary"
                        size="sm"
                        className="tabular-nums"
                      >
                        {periodCount} periods
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">1 period</span>
                    )}
                  </td>
                </tr>
                {hasMultiple && isOpen && acct.periods && (
                  <tr className="border-t border-border/30 bg-muted/20">
                    <td colSpan={9} className="px-3 py-2">
                      <div className="mb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                        Source statement periods
                      </div>
                      <table className="w-full border-collapse text-xs">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            <th className="px-2 py-1 text-left font-medium">
                              Period From
                            </th>
                            <th className="px-2 py-1 text-left font-medium">
                              Period To
                            </th>
                            <th className="px-2 py-1 text-right font-medium">
                              Opening Balance
                            </th>
                            <th className="px-2 py-1 text-right font-medium">
                              Closing Balance
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {acct.periods.map((p, pi) => (
                            <tr key={pi} className="border-t border-border/30">
                              <td className="px-2 py-1 font-mono tabular-nums">
                                {formatDate(p.periodFrom ?? "")}
                              </td>
                              <td className="px-2 py-1 font-mono tabular-nums">
                                {formatDate(p.periodTo ?? "")}
                              </td>
                              <td className="px-2 py-1 text-right font-mono tabular-nums text-muted-foreground">
                                {p.openingBalance != null
                                  ? fmt(p.openingBalance)
                                  : "-"}
                              </td>
                              <td className="px-2 py-1 text-right font-mono tabular-nums text-muted-foreground">
                                {p.closingBalance != null
                                  ? fmt(p.closingBalance)
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canonical (cross-document) Accounts Panel
// ---------------------------------------------------------------------------

/**
 * Stable identity for a canonical account. Must stay in step with the key
 * `updateCanonicalAccountField` matches on server-side.
 */
function canonicalAccountKey(account: {
  institution?: string;
  accountNumber?: string;
}): string {
  const inst = (account.institution ?? "").trim().toLowerCase();
  const num = (account.accountNumber ?? "")
    .replace(/[^A-Za-z0-9]/g, "")
    .toLowerCase();
  return `${inst}:${num}`;
}

/** Fields that form the account key — editing one re-keys the account. */
const IDENTITY_FIELDS = new Set(["institution", "accountNumber"]);

/**
 * Top-level Accounts panel shown above the document list. Renders one row per
 * real account across the entire job — built deterministically server-side
 * from `bankTransactionEntries` so duplicate statement periods can never
 * inflate the count.
 */
function CanonicalAccountsPanel({
  taskId,
  documents,
}: {
  taskId: Id<"summarizationTasks">;
  documents?: Array<{ _id: string; fileName: string }>;
}) {
  // Track the selected account by KEY, never by holding the object — canonical
  // accounts are rebuilt server-side after every edit, so a captured object goes
  // stale the moment the practitioner changes anything (the slide-over kept
  // showing the pre-edit number and history).
  //
  // Editing the institution / account number re-keys the account, and the list
  // and the mutation response don't land in a fixed order, so we hold BOTH the
  // old and new key for the duration and match on either. That keeps the sheet
  // mounted through the transition instead of flashing shut.
  const [selectedKeys, setSelectedKeys] = useState<string[] | null>(null);
  const [hiddenCols, toggleCol] = useHiddenColumns(
    "bankTxn.accounts.hiddenColumns",
  );

  const { data, isLoading } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.getCanonicalBankAccounts, {
      taskId,
    }),
  );

  const accounts = useMemo(() => (data ?? []) as CanonicalAccount[], [data]);

  const selectedAccount = useMemo(() => {
    if (selectedKeys === null) return null;
    return (
      accounts.find((a) => selectedKeys.includes(canonicalAccountKey(a))) ??
      null
    );
  }, [accounts, selectedKeys]);

  const { data: rawActions } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.getBankAccountActions, {
      taskId,
    }),
  );
  const accountActions = (rawActions ?? []) as Array<{
    id: string;
    kind: "merge" | "delete";
    sourceLabel?: string;
    targetLabel?: string;
    txnCount?: number;
    reason?: string;
    confidence?: string;
    auto?: boolean;
  }>;

  const reverseAction = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactions.publicMutations
        .reverseAccountAction,
    ),
  });

  const reconcileBankAccounts = useAction(
    api.jobs.summarization.agents.bankTransactions.reconcileAccounts
      .reconcileBankAccounts,
  );
  const [reconciling, setReconciling] = useState(false);

  const handleReconcile = () => {
    setReconciling(true);
    reconcileBankAccounts({ taskId })
      .then((r) => {
        if (r.status === "applied") {
          toast.success(
            `Merged ${r.appliedCount} account${r.appliedCount !== 1 ? "s" : ""} (${r.movedCount.toLocaleString("en-AU")} transactions reassigned)`,
          );
        } else if (r.status === "no_merges" || r.status === "nothing_to_do") {
          toast.success("Reviewed accounts — no fragments to merge.");
        } else if (r.status === "unconfigured") {
          toast.error("AI reconciliation is not configured.");
        } else {
          toast.error("Couldn't reconcile accounts — please try again.");
        }
      })
      .catch((e: Error) =>
        toast.error(e.message || "Couldn't reconcile accounts."),
      )
      .finally(() => setReconciling(false));
  };

  const handleUndo = (actionId: string) => {
    reverseAction.mutate(
      { taskId, actionId },
      {
        onSuccess: () => toast.success("Change reversed"),
        onError: (err) => toast.error(`Failed to reverse: ${err.message}`),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading accounts...
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <p className="text-xs italic text-muted-foreground">
        No accounts identified yet.
      </p>
    );
  }

  return (
    <>
      <div className="mb-2 flex items-center justify-end gap-2">
        <ColumnsMenu
          columns={ACCOUNT_COLUMNS}
          hidden={hiddenCols}
          onToggle={toggleCol}
        />
        {accountActions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <Undo2 className="h-3.5 w-3.5" />
                Changes
                <Badge variant="secondary" className="ml-0.5 px-1 text-[10px]">
                  {accountActions.length}
                </Badge>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="max-h-[28rem] w-[30rem] overflow-y-auto"
            >
              <DropdownMenuLabel>Recent account changes</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {accountActions.map((a) => (
                <DropdownMenuItem
                  key={a.id}
                  onSelect={(e) => {
                    e.preventDefault();
                    handleUndo(a.id);
                  }}
                  disabled={reverseAction.isPending}
                  className="flex-col items-stretch gap-1 py-2.5"
                >
                  <div className="flex items-start gap-2">
                    {a.kind === "delete" ? (
                      <Trash2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                    ) : (
                      <GitMerge className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-500" />
                    )}
                    <div className="min-w-0 flex-1 text-xs leading-snug">
                      {a.kind === "delete" ? (
                        <span className="font-medium">
                          Deleted {a.sourceLabel ?? "account"}
                        </span>
                      ) : (
                        <span>
                          <span className="font-medium">
                            {a.sourceLabel ?? "account"}
                          </span>
                          <span className="text-muted-foreground"> → </span>
                          <span className="font-medium">
                            {a.targetLabel ?? "account"}
                          </span>
                        </span>
                      )}
                    </div>
                    <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
                      <Undo2 className="h-3 w-3" /> Undo
                    </span>
                  </div>
                  <div className="pl-[1.375rem] text-[11px] text-muted-foreground">
                    {a.txnCount != null && (
                      <span>
                        {a.txnCount.toLocaleString("en-AU")} txn
                        {a.txnCount !== 1 ? "s" : ""}
                        {a.kind === "delete" ? " removed" : " moved"}
                      </span>
                    )}
                    {a.auto && (
                      <span className="ml-1.5 rounded-sm bg-emerald-100 px-1 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                        AI{a.confidence ? ` · ${a.confidence}` : ""}
                      </span>
                    )}
                    {a.reason && (
                      <span className="mt-0.5 block italic">{a.reason}</span>
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button
          variant="default"
          size="sm"
          onClick={handleReconcile}
          disabled={reconciling || accounts.length < 2}
          title="Use AI to merge fragmented accounts (e.g. the same number under different banks). Reversible."
          className="gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {reconciling ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          {reconciling ? "Reviewing accounts…" : "Auto-reconcile"}
        </Button>
      </div>

      {reconciling && (
        <div className="mb-2 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-100">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          Reviewing {accounts.length} account{accounts.length !== 1 ? "s" : ""}{" "}
          with AI to find fragments of the same account…
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border-2 border-border/80">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-muted/40">
            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {!hiddenCols.has("accountName") && (
                <th className="px-3 py-1.5 text-left font-medium">
                  Account Name
                </th>
              )}
              {!hiddenCols.has("accountNumber") && (
                <th className="px-3 py-1.5 text-left font-medium">
                  Account No.
                </th>
              )}
              {!hiddenCols.has("holder") && (
                <th className="px-3 py-1.5 text-left font-medium">Holder</th>
              )}
              {!hiddenCols.has("type") && (
                <th className="px-3 py-1.5 text-left font-medium">Type</th>
              )}
              {!hiddenCols.has("institution") && (
                <th className="px-3 py-1.5 text-left font-medium">
                  Institution
                </th>
              )}
              {!hiddenCols.has("txns") && (
                <th className="px-3 py-1.5 text-right font-medium">Txns</th>
              )}
              {!hiddenCols.has("totalDebits") && (
                <th className="px-3 py-1.5 text-right font-medium">
                  Total Debits
                </th>
              )}
              {!hiddenCols.has("totalCredits") && (
                <th className="px-3 py-1.5 text-right font-medium">
                  Total Credits
                </th>
              )}
              {!hiddenCols.has("closingBalance") && (
                <th className="px-3 py-1.5 text-right font-medium">
                  Closing Balance
                </th>
              )}
              {!hiddenCols.has("dates") && (
                <th className="px-3 py-1.5 text-left font-medium">Dates</th>
              )}
            </tr>
          </thead>
          <tbody>
            {accounts.map((acct, i) => {
              return (
                <tr
                  key={`${acct.institution ?? "?"}:${acct.accountNumber ?? "?"}:${i}`}
                  className="cursor-pointer border-t border-border/50 transition-colors hover:bg-muted/50"
                  onClick={() => setSelectedKeys([canonicalAccountKey(acct)])}
                >
                  {!hiddenCols.has("accountName") && (
                    <td className="px-3 py-1.5 text-xs">
                      {acct.accountName ?? "-"}
                    </td>
                  )}
                  {!hiddenCols.has("accountNumber") && (
                    <td className="px-3 py-1.5 font-mono text-xs">
                      {acct.accountNumber ?? "-"}
                    </td>
                  )}
                  {!hiddenCols.has("holder") && (
                    <td className="px-3 py-1.5 text-xs">
                      {acct.cardholderName ?? "-"}
                    </td>
                  )}
                  {!hiddenCols.has("type") && (
                    <td className="px-3 py-1.5 text-xs">
                      {formatAccountType(acct.accountType)}
                    </td>
                  )}
                  {!hiddenCols.has("institution") && (
                    <td className="px-3 py-1.5 text-xs">
                      {acct.institution ?? "-"}
                    </td>
                  )}
                  {!hiddenCols.has("txns") && (
                    <td className="px-3 py-1.5 text-right text-xs tabular-nums">
                      {acct.transactionCount.toLocaleString("en-AU")}
                    </td>
                  )}
                  {!hiddenCols.has("totalDebits") && (
                    <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-red-600 dark:text-red-400">
                      -{fmtShort(acct.totalDebits)}
                    </td>
                  )}
                  {!hiddenCols.has("totalCredits") && (
                    <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums text-emerald-600 dark:text-emerald-400">
                      +{fmtShort(acct.totalCredits)}
                    </td>
                  )}
                  {!hiddenCols.has("closingBalance") && (
                    <td className="px-3 py-1.5 text-right font-mono text-xs tabular-nums">
                      {acct.closingBalance != null
                        ? fmt(acct.closingBalance)
                        : "-"}
                    </td>
                  )}
                  {!hiddenCols.has("dates") && (
                    <td className="px-3 py-1.5 font-mono text-xs tabular-nums text-muted-foreground">
                      {acct.firstTxnDate ? formatDate(acct.firstTxnDate) : "-"}
                      {" → "}
                      {acct.lastTxnDate ? formatDate(acct.lastTxnDate) : "-"}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AccountDetailPanel
        account={selectedAccount}
        accounts={accounts}
        open={selectedAccount !== null}
        onClose={() => setSelectedKeys(null)}
        onAccountRekeyed={(nextKey) =>
          setSelectedKeys((prev) =>
            prev === null || prev.includes(nextKey) ? prev : [...prev, nextKey],
          )
        }
        documents={documents}
        taskId={taskId}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Account Detail Panel (slide-over with inline editing)
// ---------------------------------------------------------------------------

const fmtCurrency = (n?: number) =>
  n == null
    ? "\u2014"
    : "$" +
      n.toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const ACCOUNT_TYPE_OPTIONS: Record<string, string> = {
  transaction: "Transaction",
  savings: "Savings",
  term_deposit: "Term deposit",
  loan: "Loan",
  credit_card: "Credit card",
  other: "Other",
};

function EditableAccountField({
  label,
  value,
  field,
  onSave,
}: {
  label: string;
  value?: string | null;
  field: string;
  onSave: (field: string, value: string | undefined) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));

  const handleSave = () => {
    onSave(field, draft || undefined);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center justify-between gap-2 py-2">
        <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 w-36 text-sm"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
            autoFocus
          />
          <button
            onClick={handleSave}
            className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="rounded p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between gap-4 py-2">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1.5">
        <span className="text-right text-sm text-foreground">
          {value || "\u2014"}
        </span>
        <button
          onClick={() => {
            setDraft(String(value ?? ""));
            setEditing(true);
          }}
          className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

function SelectAccountField({
  label,
  value,
  options,
  field,
  onSave,
}: {
  label: string;
  value?: string;
  options: Record<string, string>;
  field: string;
  onSave: (field: string, value: string) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Select value={value ?? ""} onValueChange={(v) => onSave(field, v)}>
        <SelectTrigger className="h-7 w-40 text-sm">
          <SelectValue placeholder="\u2014" />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(options).map(([k, v]) => (
            <SelectItem key={k} value={k}>
              {v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function AccountDetailPanel({
  account,
  accounts,
  open,
  onClose,
  onAccountRekeyed,
  documents,
  taskId,
}: {
  account: CanonicalAccount | null;
  accounts?: CanonicalAccount[];
  open: boolean;
  onClose: () => void;
  /** Called when an edit changes the account's key, so the parent keeps it selected. */
  onAccountRekeyed: (nextKey: string) => void;
  documents?: Array<{ _id: string; fileName: string }>;
  taskId: Id<"summarizationTasks">;
}) {
  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [allocateTarget, setAllocateTarget] = useState<string>("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const updateField = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactions.publicMutations
        .updateCanonicalAccountField,
    ),
  });

  const mergeAccounts = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactions.publicMutations
        .mergeCanonicalAccounts,
    ),
  });

  const deleteAccount = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactions.publicMutations
        .deleteCanonicalAccount,
    ),
  });

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    if (!account?.changeHistory) return [];
    const ids = new Set<Id<"users">>();
    for (const change of account.changeHistory) {
      if (change.userId) ids.add(change.userId);
    }
    return Array.from(ids);
  }, [account?.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });

  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  if (!account) return null;

  const accountKey = canonicalAccountKey(account);

  const handleFieldSave = (field: string, value: string | undefined) => {
    // Correcting the institution / account number re-keys the account. Tell the
    // parent up front so the slide-over follows the account across the rebuild
    // rather than losing track of it and showing stale values.
    if (IDENTITY_FIELDS.has(field)) {
      onAccountRekeyed(
        canonicalAccountKey({
          institution: field === "institution" ? value : account.institution,
          accountNumber:
            field === "accountNumber" ? value : account.accountNumber,
        }),
      );
    }
    updateField.mutate(
      { taskId, accountKey, field, value: value ?? null },
      {
        onSuccess: (result) => {
          if (result.updatedTransactionCount > 0) {
            toast.success(
              `Account updated — ${result.updatedTransactionCount.toLocaleString("en-AU")} transaction${result.updatedTransactionCount !== 1 ? "s" : ""} reassigned`,
            );
          }
        },
        onError: (err) => {
          toast.error(`Failed to update: ${err.message}`);
        },
      },
    );
  };

  const accountDisplayLabel =
    [account.institution, account.accountName, account.accountNumber]
      .filter(Boolean)
      .join(" — ") || "Account";
  const memberKeys = account.memberKeys ?? [];
  // Other active accounts a practitioner can allocate this one into.
  const otherAccounts = (accounts ?? []).filter((a) => a !== account);
  const isMutating = mergeAccounts.isPending || deleteAccount.isPending;

  const handleAllocate = () => {
    const target = otherAccounts[Number(allocateTarget)];
    if (!target) return;
    const targetLabel =
      [target.institution, target.accountName, target.accountNumber]
        .filter(Boolean)
        .join(" — ") || "Account";
    mergeAccounts.mutate(
      {
        taskId,
        sourceMemberKeys: memberKeys,
        sourceInstitution: account.institution,
        sourceAccountNumber: account.accountNumber,
        sourceDocumentIds: account.sourceDocumentIds ?? [],
        sourceLabel: accountDisplayLabel,
        targetInstitution: target.institution,
        targetAccountNumber: target.accountNumber,
        targetLabel,
      },
      {
        onSuccess: (r) => {
          toast.success(
            `Allocated ${r.movedCount} transactions into ${targetLabel}`,
          );
          onClose();
        },
        onError: (err) => toast.error(`Failed to allocate: ${err.message}`),
      },
    );
  };

  const handleDelete = () => {
    deleteAccount.mutate(
      {
        taskId,
        memberKeys,
        accountInstitution: account.institution,
        accountNumber: account.accountNumber,
        sourceDocumentIds: account.sourceDocumentIds ?? [],
        accountLabel: accountDisplayLabel,
      },
      {
        onSuccess: (r) => {
          toast.success(
            r.removedCount > 0
              ? `Deleted account (${r.removedCount} transactions removed)`
              : "Account removed",
          );
          setConfirmingDelete(false);
          onClose();
        },
        onError: (err) => toast.error(`Failed to delete: ${err.message}`),
      },
    );
  };

  const sourceDocNames = (account.sourceDocumentIds ?? [])
    .map((id) => {
      const doc = documents?.find((d) => d._id === id);
      return { id, name: doc?.fileName ?? id.slice(0, 12) + "..." };
    })
    .filter(Boolean);

  const net = account.totalCredits - account.totalDebits;

  return (
    <>
      <SlideOverPanel open={open} onClose={onClose}>
        {/* Header */}
        <div className="sticky top-0 z-10 border-b bg-background/95 px-5 pb-3 pt-5 backdrop-blur-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold leading-snug text-foreground">
                {account.accountName || account.institution || "Bank Account"}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>
          <div className="mt-2 flex items-center gap-2">
            {account.accountType && (
              <Badge variant="soft" size="sm" color="blue">
                {ACCOUNT_TYPE_OPTIONS[account.accountType] ??
                  account.accountType}
              </Badge>
            )}
            {account.institution && (
              <Badge variant="soft" size="sm" color="gray">
                {account.institution}
              </Badge>
            )}
          </div>
        </div>

        <div className="px-5 pb-8">
          {/* Totals banner */}
          <div className="mt-4 rounded-lg border bg-muted/30 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Total Debits
                </div>
                <span className="font-mono text-xl font-bold tabular-nums text-red-600 dark:text-red-400">
                  -{fmtCurrency(account.totalDebits)}
                </span>
              </div>
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Total Credits
                </div>
                <span className="font-mono text-xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                  +{fmtCurrency(account.totalCredits)}
                </span>
              </div>
            </div>
            <div className="mt-3 border-t pt-3">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                Net Position
              </div>
              <span
                className={`font-mono text-lg font-bold tabular-nums ${net >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
              >
                {net >= 0 ? "+" : "-"}
                {fmtCurrency(Math.abs(net))}
              </span>
            </div>
          </div>

          {/* Account details — editable */}
          <div className="pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]/70">
            Account Details
          </div>
          <div className="divide-y">
            <EditableAccountField
              label="Account Name"
              value={account.accountName}
              field="accountName"
              onSave={handleFieldSave}
            />
            <EditableAccountField
              label="Institution"
              value={account.institution}
              field="institution"
              onSave={handleFieldSave}
            />
            <EditableAccountField
              label="Account Number"
              value={account.accountNumber}
              field="accountNumber"
              onSave={handleFieldSave}
            />
            <EditableAccountField
              label="BSB"
              value={account.bsb}
              field="bsb"
              onSave={handleFieldSave}
            />
            <EditableAccountField
              label="Account Holder"
              value={account.cardholderName}
              field="cardholderName"
              onSave={handleFieldSave}
            />
            <SelectAccountField
              label="Account Type"
              value={account.accountType}
              options={ACCOUNT_TYPE_OPTIONS}
              field="accountType"
              onSave={handleFieldSave}
            />
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">
                Transactions
              </span>
              <span className="text-sm tabular-nums text-foreground">
                {account.transactionCount.toLocaleString("en-AU")}
              </span>
            </div>
          </div>
          <div className="mt-2 border-b" />

          {/* Manage account — allocate (merge) / delete */}
          <div className="pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]/70">
            Manage Account
          </div>
          <div className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-3">
            <div className="flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <label className="text-[11px] text-muted-foreground">
                  Allocate into another account
                </label>
                <Select
                  value={allocateTarget}
                  onValueChange={setAllocateTarget}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Choose target account…" />
                  </SelectTrigger>
                  <SelectContent>
                    {otherAccounts.map((a, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {[a.institution, a.accountName, a.accountNumber]
                          .filter(Boolean)
                          .join(" — ") || "Account"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                disabled={!allocateTarget || isMutating}
                onClick={handleAllocate}
              >
                <GitMerge className="h-3.5 w-3.5" /> Allocate
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Moves this account&apos;s{" "}
              {account.transactionCount.toLocaleString("en-AU")} transactions
              into the chosen account. Reversible from the accounts list.
            </p>

            <div className="mt-1 border-t pt-2">
              {confirmingDelete ? (
                <div className="flex items-center gap-2">
                  <span className="flex-1 text-xs text-foreground">
                    Delete this account and remove its transactions from all
                    totals?
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmingDelete(false)}
                    disabled={isMutating}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="gap-1.5"
                    onClick={handleDelete}
                    disabled={isMutating}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Confirm delete
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={isMutating}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete account
                </Button>
              )}
            </div>
          </div>

          {/* Date coverage */}
          <div className="pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]/70">
            Date Coverage
          </div>
          <div className="divide-y">
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">
                First Transaction
              </span>
              <span className="font-mono text-sm tabular-nums text-foreground">
                {account.firstTxnDate
                  ? formatDate(account.firstTxnDate)
                  : "\u2014"}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">
                Last Transaction
              </span>
              <span className="font-mono text-sm tabular-nums text-foreground">
                {account.lastTxnDate
                  ? formatDate(account.lastTxnDate)
                  : "\u2014"}
              </span>
            </div>
          </div>
          <div className="mt-2 border-b" />

          {/* Source documents */}
          {sourceDocNames.length > 0 && (
            <>
              <div className="pb-1.5 pt-6 text-xs font-medium uppercase tracking-widest text-[#121212]/70">
                Source Documents
              </div>
              <div className="flex flex-col gap-2 pt-2.5">
                {sourceDocNames.map((doc) => (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() =>
                      setViewingDocumentId(doc.id as Id<"documents">)
                    }
                    className="group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors hover:bg-muted/50"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">
                        {doc.name}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
                  </button>
                ))}
              </div>
              <div className="mt-4 border-b" />
            </>
          )}

          {/* Change history */}
          <div className="mt-4">
            {account.changeHistory && account.changeHistory.length > 0 ? (
              <ChangeHistory
                history={account.changeHistory}
                usersById={historyUsersById}
                formatters={BANK_TRANSACTION_FIELD_FORMATTERS}
                fieldLabels={BANK_TRANSACTION_FIELD_LABELS}
              />
            ) : (
              <div>
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                  History
                </h4>
                <p className="mt-2 text-xs italic text-muted-foreground">
                  No changes recorded yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </SlideOverPanel>

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Document Section (collapsible per-document view)
// ---------------------------------------------------------------------------

function DocumentSection({
  doc,
  jobId,
  isOpen,
  onToggle,
  isViewer,
}: {
  doc: DocumentMeta;
  jobId: Id<"jobs">;
  isOpen: boolean;
  onToggle: () => void;
  isViewer?: boolean;
}) {
  const entityName = doc.entity?.name;
  const allAccounts = useMemo(
    () => dedupeAccountsByNumber(doc.accounts ?? []),
    [doc.accounts],
  );

  const { data: rawTransactions } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listBankTransactionsForDocument,
      { jobId, sourceDocumentId: doc.documentId },
    ),
  );

  const accounts = useMemo(() => {
    if (!rawTransactions || rawTransactions.length === 0) return allAccounts;
    const txnAccountNumbers = new Set(
      (rawTransactions as BankTxnEntry[])
        .map((t) => normalizeAccountNumber(t.accountNumber))
        .filter((n) => n.length > 0),
    );
    if (txnAccountNumbers.size === 0) return allAccounts;
    const filtered = allAccounts.filter((acct) => {
      const norm = normalizeAccountNumber(acct.accountNumber);
      if (!norm) return true;
      return txnAccountNumbers.has(norm);
    });
    return filtered.length > 0 ? filtered : allAccounts;
  }, [allAccounts, rawTransactions]);

  const displayTitle = useMemo(() => {
    if (accounts.length === 0) return entityName || doc.fileName;
    if (accounts.length === 1) {
      const acct = accounts[0];
      const parts: string[] = [];
      if (acct.institution) parts.push(acct.institution);
      if (acct.accountName) parts.push(acct.accountName);
      else if (acct.accountNumber) parts.push(acct.accountNumber);
      return parts.length > 0 ? parts.join(" — ") : entityName || doc.fileName;
    }
    const institutions = [
      ...new Set(accounts.map((a) => a.institution).filter(Boolean)),
    ];
    if (institutions.length === 1)
      return `${institutions[0]} (${accounts.length} accounts)`;
    return entityName || `${accounts.length} accounts`;
  }, [accounts, entityName, doc.fileName]);

  return (
    <div className="overflow-hidden rounded-lg border">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-muted/50"
      >
        <div className="flex min-w-0 items-center gap-2">
          <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-medium">{displayTitle}</span>
          {entityName && displayTitle !== entityName && (
            <span className="hidden truncate text-xs text-muted-foreground sm:inline">
              ({entityName})
            </span>
          )}
        </div>
        <div className="ml-4 flex shrink-0 items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {doc.transactionCount} txn{doc.transactionCount !== 1 ? "s" : ""}
          </span>
          {accounts.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {accounts.length} acct{accounts.length !== 1 ? "s" : ""}
            </span>
          )}
          <svg
            className={`h-3 w-3 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="space-y-5 border-t px-4 py-4">
          {accounts.length > 0 && (
            <Section
              icon={<Landmark className="h-3.5 w-3.5 text-muted-foreground" />}
              title="Accounts"
              count={accounts.length}
            >
              <DocAccountsTable accounts={accounts} />
            </Section>
          )}

          <Section
            icon={<FileText className="h-3.5 w-3.5 text-muted-foreground" />}
            title="Transactions"
            count={doc.transactionCount}
          >
            <DocumentTransactions
              jobId={jobId}
              documentId={doc.documentId}
              isViewer={isViewer}
            />
          </Section>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

interface BankTransactionsDocumentsPanelProps {
  jobId: Id<"jobs">;
  taskId: Id<"summarizationTasks">;
  isRunning: boolean;
  isViewer?: boolean;
}

export function BankTransactionsDocumentsPanel({
  jobId,
  taskId,
  isRunning,
  isViewer,
}: BankTransactionsDocumentsPanelProps) {
  const [openDocs, setOpenDocs] = useState<Set<string>>(new Set());

  const { data: documents, isLoading } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getBankTransactionDocuments,
      { taskId },
    ),
  );

  const docs = useMemo(() => {
    const rawDocs = (documents ?? []) as DocumentMeta[];
    if (rawDocs.length === 0) return rawDocs;
    return [...rawDocs].sort((a, b) => {
      const instA = (a.accounts?.[0]?.institution ?? "").toLowerCase();
      const instB = (b.accounts?.[0]?.institution ?? "").toLowerCase();
      if (instA !== instB) return instA.localeCompare(instB);
      const nameA = (a.accounts?.[0]?.accountName ?? "").toLowerCase();
      const nameB = (b.accounts?.[0]?.accountName ?? "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [documents]);

  const { data: canonicalAccountsData } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.getCanonicalBankAccounts, {
      taskId,
    }),
  );
  const canonicalAccounts = (canonicalAccountsData ?? []) as CanonicalAccount[];

  const toggleDoc = useCallback((docId: string) => {
    setOpenDocs((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading...
      </div>
    );
  }

  if (docs.length === 0) {
    if (isRunning) {
      return (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Processing bank transactions...
        </div>
      );
    }
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">
        No bank transaction documents found.
      </div>
    );
  }

  const totalTxns = docs.reduce((s, d) => s + d.transactionCount, 0);
  // Canonical account count when available; falls back to summed per-doc
  // counts for tasks created before the canonical field was populated.
  const totalAccounts =
    canonicalAccounts.length > 0
      ? canonicalAccounts.length
      : docs.reduce((s, d) => s + (d.accounts?.length ?? 0), 0);

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-4">
      <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        {/* Header */}
        <div className="px-5 pb-4 pt-5">
          <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Transactions Extracted
          </p>
          <p className="text-2xl font-bold tabular-nums text-foreground">
            {totalTxns.toLocaleString()}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {totalAccounts} account{totalAccounts !== 1 ? "s" : ""} ·{" "}
            {docs.length} document{docs.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Canonical accounts (deduped across documents) */}
        <div className="border-t border-border px-4 py-4">
          <Section
            icon={<Landmark className="h-3.5 w-3.5 text-muted-foreground" />}
            title="Accounts Extracted"
            count={canonicalAccounts.length}
            defaultOpen={true}
          >
            <CanonicalAccountsPanel
              taskId={taskId}
              documents={docs.map((d) => ({
                _id: d.documentId,
                fileName: d.fileName,
              }))}
            />
          </Section>
        </div>

        {/* Document list */}
        <div className="flex min-h-0 flex-1 flex-col border-t border-border">
          <div className="flex-1 overflow-auto">
            <div className="flex flex-col gap-3 p-4">
              {docs.map((doc) => (
                <DocumentSection
                  key={doc.documentId}
                  doc={doc}
                  jobId={jobId}
                  isOpen={openDocs.has(doc.documentId)}
                  onToggle={() => toggleDoc(doc.documentId)}
                  isViewer={isViewer}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border px-5 py-3">
          <p className="text-center text-xs text-muted-foreground">
            Click any document to view accounts and transactions
          </p>
        </div>
      </div>
    </div>
  );
}
